import { useCurrentUser } from '@/providers/CurrentUserProvider';
import {
  AuthRolesAndExperimentalFeatures,
  EnterpriseRoleEnum,
  FeatureEnum,
  PermissionEnum,
} from '@/graphql/generated';
import { useProjectContext } from '@/providers/ProjectContextProvider';

export function usePermissionChecker() {
  const { currentUser, permissions } = useCurrentUser();

  const permissionHierarchy: Record<PermissionEnum, number> = {
    [PermissionEnum.None]: 0,
    [PermissionEnum.Read]: 1,
    [PermissionEnum.Write]: 2,
  };
  const projectContext = useProjectContext();

  const isGranted = (
    feature: FeatureEnum,
    requiredPermission: PermissionEnum = PermissionEnum.Read,
    strictComparison = false,
    enterpriseId?: string,
  ): boolean => {
    const id = enterpriseId ?? projectContext?.enterprise?.id;
    if (projectContext?.enterprise?.isTemplate) {
      return (
        (hasRole(AuthRolesAndExperimentalFeatures.Administrator) ||
          hasRole(AuthRolesAndExperimentalFeatures.Coach)) &&
        projectContext?.enterprise.ownedBy?.id === currentUser?.company?.id
      );
    }

    const enterprisePermissions = permissions.find(
      (permission) => permission.enterpriseId === id,
    );

    if (!enterprisePermissions) {
      return false;
    }

    const userPermission = enterprisePermissions.permissions[feature];

    if (!userPermission) {
      return false;
    }

    if (strictComparison) {
      return userPermission === requiredPermission;
    }

    return (
      permissionHierarchy[userPermission] >=
      permissionHierarchy[requiredPermission]
    );
  };

  const hasRole = (
    role: AuthRolesAndExperimentalFeatures,
    allowSuperAdminByPass = true,
  ) => {
    if (!currentUser?.auth) {
      return false;
    }
    return (
      currentUser.auth.roles.includes(role) ||
      (allowSuperAdminByPass &&
        currentUser.auth.roles.includes(
          AuthRolesAndExperimentalFeatures.Superadmin,
        ))
    );
  };

  const hasEnterpriseRole = (role: EnterpriseRoleEnum) => {
    const enterpriseId = projectContext?.enterprise?.id;

    return (
      currentUser?.userAccesses?.some(
        (userAccess) =>
          userAccess.enterprise?.id === enterpriseId &&
          userAccess.role.role === role,
      ) || false
    );
  };

  return { isGranted, hasRole, loading: !currentUser, hasEnterpriseRole };
}
